import {
  createRootRoute, createRoute, createRouter,
  RouterProvider, Outlet, redirect,
} from '@tanstack/react-router';
import { Login } from './pages/Login.tsx';
import { AcceptInvite } from './pages/AcceptInvite.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { SsoHandoff } from './pages/SsoHandoff.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { Profile } from './pages/Profile.tsx';
import { Builder } from './pages/Builder.tsx';
import { Content } from './pages/Content.tsx';
import { AppLayout } from './components/AppLayout.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { useAuthStore } from './store/auth.ts';

// Minimal skeleton layout. Product chrome (nav/header) is added per-milestone as
// WapSite's admin builder is built out.
function RootLayout(): JSX.Element {
  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <main id="main-content" tabIndex={-1} className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const requireAuth = (): void => {
  if (!useAuthStore.getState().accessToken) throw redirect({ to: '/login' });
};

// Public routes
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/invite/$token', component: AcceptInvite });
const ssoHandoffRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sso/handoff', component: SsoHandoff });
const forgotPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPassword });
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reset-password/$token', component: ResetPassword });

// Authed routes — the builder chrome (sidebar nav) wraps each page via AppLayout.
// The whole builder is one tabbed page at '/' (Website); Profile is the only
// other authed page. URLs stay flat.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/',
  beforeLoad: requireAuth,
  component: () => <AppLayout><Builder /></AppLayout>,
});
const contentRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/content',
  beforeLoad: requireAuth,
  validateSearch: (search: Record<string, unknown>): { s?: string } => ({
    s: typeof search.s === 'string' ? search.s : undefined,
  }),
  component: () => <AppLayout><Content /></AppLayout>,
});
const profileRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/profile',
  beforeLoad: requireAuth,
  component: () => <AppLayout><Profile /></AppLayout>,
});

// /dashboard, /theme and old /section/* links now all live in the '/' tabbed
// page — redirect legacy bookmarks home.
const dashboardRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/dashboard',
  beforeLoad: () => { throw redirect({ to: '/' }); },
});
const themeRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/theme',
  beforeLoad: () => { throw redirect({ to: '/' }); },
});
const sectionRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/section/$group',
  beforeLoad: () => { throw redirect({ to: '/' }); },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  ssoHandoffRoute, inviteRoute, forgotPasswordRoute, resetPasswordRoute,
  indexRoute, contentRoute, profileRoute,
  dashboardRedirectRoute, themeRedirectRoute, sectionRedirectRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}

export function AppRouter(): JSX.Element { return <RouterProvider router={router} />; }
